// важно: базовые стили
import { useEffect, useRef } from "react";

import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

type Props = {
  latitude: number;
  longitude: number;
  zoom?: number;
  viewOnly?: boolean;
  className?: string;
  height?: number | string;
  onClick?: (coords: { latitude: number; longitude: number }) => void;
  language?: "ru" | "en" | string;
};

const pinUrl = new URL("/src/shared/assets/images/map_pin.png", import.meta.url).href;

export default function MapboxGlContainer({
  latitude,
  longitude,
  zoom = 12,
  onClick,
  viewOnly = true,
  className,
  height = 322,
  language = "ru",
}: Props) {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onClickRef = useRef(onClick);

  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  // helper: применить язык подписей
  const applyLanguage = (map: mapboxgl.Map, lang: string) => {
    const style = map.getStyle();
    const layers = style?.layers ?? [];
    layers.forEach((l: any) => {
      if (l.type === "symbol" && l.layout && l.layout["text-field"]) {
        // переключаем 'text-field' на name_<lang>, иначе fallback на name
        map.setLayoutProperty(l.id, "text-field", ["coalesce", ["get", `name_${lang}`], ["get", "name"]]);
      }
    });
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // быстрый рантайм-чек на transform у предков
    let el: HTMLElement | null = containerRef.current;
    while (el) {
      const tr = getComputedStyle(el).transform;
      if (tr && tr !== "none" && tr !== "matrix(1, 0, 0, 1, 0, 0)") {
        console.warn(
          "[Mapbox] Родительский контейнер имеет CSS transform. Клики могут смещаться. " +
            "Перенесите карту вне transform/scale (например, портал в body).",
        );
        break;
      }
      el = el.parentElement;
    }

    mapboxgl.accessToken = import.meta.env.VITE_APP_MAPBOX_ACCESS_TOKEN;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [longitude, latitude], // [lng, lat]
      zoom,
    });

    // курсоры
    const canvas = map.getCanvas();
    const enter = () => (canvas.style.cursor = "pointer");
    const leave = () => (canvas.style.cursor = viewOnly ? "grab" : "pointer");
    const down = () => (canvas.style.cursor = "grabbing");
    const up = () => (canvas.style.cursor = "pointer");

    if (!viewOnly) {
      map.getCanvasContainer().style.cursor = "pointer";
      map.on("mouseenter", enter);
      map.on("mouseleave", leave);
      map.on("mousedown", down);
      map.on("mouseup", up);
    } else {
      map.getCanvasContainer().style.cursor = "grab";
    }

    // маркер
    const elPin = document.createElement("div");
    elPin.style.cssText = `
      background-image: url(${pinUrl});
      width:30px;height:30px;background-size:contain;background-repeat:no-repeat;
    `;

    const marker = new mapboxgl.Marker({ element: elPin, anchor: "bottom", draggable: !viewOnly })
      .setLngLat([longitude, latitude])
      .addTo(map);
    markerRef.current = marker;

    // клик по карте
    const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      marker.setLngLat([lng, lat]);
      onClickRef.current?.({ longitude: lng, latitude: lat });
      // локально плавно центрируем (чтобы не зависеть от внешней синхронизации)
      map.easeTo({ center: [lng, lat] });
    };
    if (!viewOnly) map.on("click", handleMapClick);

    // dragend
    const handleDragEnd = () => {
      const { lng, lat } = marker.getLngLat();
      onClickRef.current?.({ longitude: lng, latitude: lat });
      map.easeTo({ center: [lng, lat] });
    };
    if (!viewOnly) marker.on("dragend", handleDragEnd);

    // resize при появлении/изменении размеров
    const ro = new ResizeObserver(() => {
      map.resize();
    });
    ro.observe(containerRef.current);

    // resize и язык после стиля
    const onStyle = () => {
      map.resize();
      applyLanguage(map, language); // ru по умолчанию
    };
    map.on("load", onStyle);
    map.on("styledata", onStyle);

    mapRef.current = map;

    return () => {
      if (!viewOnly) marker.off("dragend", handleDragEnd);
      if (!viewOnly) {
        map.off("click", handleMapClick);
        map.off("mouseenter", enter);
        map.off("mouseleave", leave);
        map.off("mousedown", down);
        map.off("mouseup", up);
      }
      map.off("load", onStyle);
      map.off("styledata", onStyle);
      ro.disconnect();
      map.remove();
    };
    // инициализируем один раз
  }, []);

  // синхронизация с внешними координатами
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    marker.setLngLat([longitude, latitude]);
    map.flyTo({ center: [longitude, latitude], zoom });
  }, [longitude, latitude]);

  // отдельный контроль зума
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setZoom(zoom);
  }, [zoom]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: "100%", height, minHeight: height, borderRadius: 16, overflow: "hidden" }}
    />
  );
}
